import { CheckSquare, ExternalLink, GitPullRequest, Square } from 'lucide-react';
import type { ApprovalDetail } from '../../services/api';
import { useAppStore } from '../../store/useAppStore';

/**
 * What a request is about, so it can be decided: the item, where it sits (project › epic ›
 * feature › story), what it was meant to do, the evidence it was done (its checklist and
 * linked pull requests), why auto-close did not take it, and who asked -- by name.
 *
 * "Move to done, requested by agent:apitoken:3d53…" is not something anyone can approve.
 */
export default function ApprovalContext({ detail }: { detail: ApprovalDetail }) {
  const setDetailItemId = useAppStore((s) => s.setDetailItemId);
  const item = detail.item;
  if (!item?.id) return null;

  return (
    <div className="rounded border border-gray-200 dark:border-gray-700 p-3 space-y-2 text-xs">
      {item.chain && item.chain.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 text-gray-500">
          {item.chain.map((c, i) => (
            <span key={c.id} className="flex items-center gap-1">
              {i > 0 && <span className="text-gray-300 dark:text-gray-600">›</span>}
              <span className="font-mono">{c.key}</span>
              {c.type !== 'project' && <span className="truncate max-w-[14rem]">{c.title}</span>}
              {c.type === 'project' && c.title && <span className="truncate max-w-[14rem]">{c.title}</span>}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {item.key && <span className="font-mono text-xs text-gray-500 mr-1.5">{item.key}</span>}
            {item.title}
          </div>
          <div className="text-gray-500 mt-0.5">{item.type} · now <strong>{item.status}</strong></div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button type="button" onClick={() => setDetailItemId(item.id)}
            className="text-accent hover:underline">Open</button>
          <a href={item.url} target="_blank" rel="noreferrer" title="Open in a new tab"
            className="text-gray-400 hover:text-accent"><ExternalLink className="w-3.5 h-3.5" /></a>
        </div>
      </div>

      {item.description ? (
        <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap line-clamp-6">{item.description}</p>
      ) : null}

      {item.checklist && item.checklist.length > 0 && (
        <div>
          <div className="text-gray-500 mb-0.5">
            Done when ({item.checklist.filter((c) => c.checked).length}/{item.checklist.length} checked)
          </div>
          <ul className="space-y-0.5">
            {item.checklist.map((c, i) => (
              <li key={i} className="flex items-start gap-1.5 text-gray-800 dark:text-gray-200">
                {c.checked
                  ? <CheckSquare className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-px" />
                  : <Square className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-px" />}
                <span>{c.text || <em className="text-gray-400">(no text)</em>}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {item.pullRequests && item.pullRequests.length > 0 ? (
        <div className="space-y-0.5">
          {item.pullRequests.map((p) => (
            <a key={`${p.repo}#${p.number}`} href={p.url || `https://github.com/${p.repo}/pull/${p.number}`}
              target="_blank" rel="noreferrer"
              className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300 hover:text-accent">
              <GitPullRequest className="w-3.5 h-3.5 shrink-0" />
              <span className="font-mono">{p.repo}#{p.number}</span>
              {p.title && <span className="truncate">{p.title}</span>}
              {p.state && <span className="text-gray-400">({p.state})</span>}
            </a>
          ))}
        </div>
      ) : detail.agentFinish ? (
        <p className="text-gray-500">No pull request is linked to this item.</p>
      ) : null}

      {detail.agentFinish && (detail.autoCloseCheck?.length ?? 0) > 0 && (
        <p className="text-amber-700 dark:text-amber-300">
          Auto-close did not close it: {detail.autoCloseCheck!.join('; ')}.
        </p>
      )}
      {detail.agentFinish && !item.autoCloseOn && (
        <p className="text-gray-500">Auto-close is off for this project, so a person closes agent work.</p>
      )}
    </div>
  );
}
